// `<MeshGradientBackground>` — the PCG Design System's live animated background,
// a real WebGL mesh-gradient (not a screenshot/CSS approximation), reproduced
// as a genuine MDX-composable React component. It drives the vendored
// `whatamesh` engine (../lib/vendor/whatamesh.ts — the SAME engine
// phoenixcourt.vc ships) through the same public surface the original
// legacy-report script used: initGradient / play / pause / resize / amp /
// seed / the `--gradient-color-*` CSS vars it reads.
//
// Ships its own showcase chrome (mark, title) so an MDX author can drop it in
// with zero markup: `<MeshGradientBackground/>`. Runs continuously (infinite
// loop) — reduced-motion users get a single still frame instead, per
// `prefersReducedMotion()` below. Index 0 is the base fill; 1-3 are mesh
// "wave" layers blended on top at noise-driven opacity (so the base dominates
// and the others read as drifting blobs) — this is the phoenixcourt.vc
// companies-page wash, the only palette the PCG Design System ships.
import { useEffect, useMemo, useRef } from "react";
import { Gradient } from "./vendor/whatamesh";

const WARM_COLORS = ["#F0CDA8", "#FDC87A", "#EA607E", "#F2B48F"];
function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

function hexToRgb01(hex: string): [number, number, number] {
  const int = parseInt(hex.slice(1), 16);
  return [((int >> 16) & 255) / 255, ((int >> 8) & 255) / 255, (255 & int) / 255];
}

let uid = 0;

// The vendored `whatamesh` engine (../lib/vendor/whatamesh.ts) is de-babelled
// output — its constructor sets fields imperatively (`e(this, "amp", 320)`
// helper calls) rather than declaring typed class members, so TS sees
// `new Gradient()` as exposing none of them. This interface names only the
// subset of that surface this file actually reads/writes/calls; casting
// through `unknown` documents the exact contract instead of disabling
// type-checking on the whole instance.
interface GradientHandle {
  amp: number;
  seed: number;
  sectionColors: [number, number, number][];
  initGradientColors: () => void;
  waitForCssVars: () => void;
  init: () => void;
  addIsLoadedClass: () => void;
  minigl: { setSize(width: number, height: number): void; setOrthographicCamera(): void } | undefined;
  mesh:
    | {
        geometry: { setTopology(x: number, y: number): void; setSize(w: number, h: number): void };
        material: { uniforms: { u_shadow_power: { value: number } } };
      }
    | undefined;
  width: number;
  height: number;
  xSegCount: number;
  ySegCount: number;
  conf: { density: [number, number] };
  resize: () => void;
  initGradient: (selector: string) => void;
  play: () => void;
  pause: () => void;
}

// Attach a live mesh-gradient to `host`, sized to its bounding box via
// ResizeObserver (the vendored engine sizes itself to `window` with a fixed
// height — it was written for a full-bleed hero — so `resize` is overridden
// to track the host instead).
function attachGradient(host: HTMLElement, colors: string[]) {
  if (getComputedStyle(host).position === "static") host.style.position = "relative";
  host.style.overflow = host.style.overflow || "hidden";

  const canvas = document.createElement("canvas");
  canvas.id = `pcg-bg-canvas-${++uid}`;
  Object.assign(canvas.style, {
    position: "absolute",
    inset: "0",
    width: "100%",
    height: "100%",
    display: "block",
    pointerEvents: "none",
  });
  host.prepend(canvas);

  const gradient = new Gradient() as unknown as GradientHandle;
  gradient.amp = 320;
  gradient.seed = 5;
  // The vendored engine's own `initGradientColors()` round-trips through
  // `getComputedStyle(canvas).getPropertyValue("--gradient-color-N")` and
  // `.filter(Boolean)`s the result — under real interaction load one read can
  // come back empty, shrinking `sectionColors` and leaving `u_waveLayers.value`
  // an empty array, which crashes `Material` construction (`getDeclaration` on
  // `undefined`). Hand the fixed palette straight to the engine instead of
  // relying on that CSS round-trip.
  gradient.initGradientColors = () => {
    gradient.sectionColors = colors.map(hexToRgb01);
  };
  // `waitForCssVars()` polls up to 200 animation frames (~3.3s) for
  // `--gradient-color-1..4`, which this app never defines, before calling
  // `init()` (the step that builds `gradient.mesh` and first sizes the
  // canvas). Since colors are already hardcoded above, skip the wait and
  // init immediately — otherwise the canvas sits at the browser's default
  // 300x150 backing store, stretched across the host, for that whole delay.
  gradient.waitForCssVars = () => {
    gradient.init();
    gradient.addIsLoadedClass();
  };

  const applySize = () => {
    const r = host.getBoundingClientRect();
    const w = Math.max(1, Math.round(r.width));
    const h = Math.max(1, Math.round(r.height));
    if (!gradient.minigl || !gradient.mesh) return;
    gradient.width = w;
    gradient.height = h;
    gradient.minigl.setSize(w, h);
    gradient.minigl.setOrthographicCamera();
    gradient.xSegCount = Math.ceil(w * gradient.conf.density[0]);
    gradient.ySegCount = Math.ceil(h * gradient.conf.density[1]);
    gradient.mesh.geometry.setTopology(gradient.xSegCount, gradient.ySegCount);
    gradient.mesh.geometry.setSize(w, h);
    gradient.mesh.material.uniforms.u_shadow_power.value = w < 600 ? 5 : 6;
  };
  gradient.resize = applySize;
  gradient.initGradient(`#${canvas.id}`);

  const ro = new ResizeObserver(() => {
    if (gradient.minigl && gradient.mesh) applySize();
  });
  ro.observe(host);

  return {
    canvas,
    play: () => gradient.play(),
    pause: () => gradient.pause(),
    destroy() {
      ro.disconnect();
      gradient.pause();
      canvas.remove();
    },
  };
}

const CSS = `
.pcg-bg-showcase{margin:8px 0 22px;border:1px solid var(--border);border-radius:var(--r-card);overflow:hidden;box-shadow:var(--shadow-surface)}
.pcg-bg-hero{position:relative;min-height:320px;display:flex;align-items:flex-end;overflow:hidden;background:#F0CDA8}
.pcg-bg-hero canvas{position:absolute;inset:0;width:100%;height:100%;display:block}
.pcg-bg-hero-in{position:relative;z-index:1;display:flex;align-items:center;gap:16px;padding:28px 30px}
.pcg-bg-mark{width:46px;height:46px;border-radius:12px;flex:none;background:conic-gradient(from 200deg,#FEDC05,#F39300,#E10A7C,#602482,#1D9BF0,#008B36,#FEDC05);box-shadow:0 8px 24px rgba(20,20,40,.20)}
.pcg-bg-kicker{font-size:12px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#5a4632}
.pcg-bg-title{font-size:27px;font-weight:700;letter-spacing:-.01em;color:#241a10;line-height:1.1}
.pcg-bg-live{display:inline-flex;align-items:center;gap:6px;margin-top:6px;font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#3c2f1f}
.pcg-bg-live::before{content:"";width:7px;height:7px;border-radius:50%;background:#0bbf5a;box-shadow:0 0 0 0 rgba(11,191,90,.6);animation:pcgPulse 1.8s infinite}
@keyframes pcgPulse{0%{box-shadow:0 0 0 0 rgba(11,191,90,.55)}70%{box-shadow:0 0 0 7px rgba(11,191,90,0)}100%{box-shadow:0 0 0 0 rgba(11,191,90,0)}}
@media(prefers-reduced-motion:reduce){.pcg-bg-live::before{animation:none}}
`;

export function MeshGradientBackground({
  title = "Animated Background",
  kicker = "PCG Design System",
  bare = false,
  hostClassName,
  children,
  colors = WARM_COLORS,
}: {
  title?: string;
  kicker?: string;
  /** Skip the showcase card/mark/kicker/title/badge chrome — just mount the
   * gradient canvas into a host div. For callers with their own chrome (e.g.
   * an existing hero/banner surface) that only want the animated background
   * itself. */
  bare?: boolean;
  hostClassName?: string;
  children?: React.ReactNode;
  /** Palette override for callers animating a different existing gradient
   * (e.g. `BRAND_COLORS` for the app-shell's pink→orange hero/banner). Defaults
   * to the PCG Design System warm palette used by the report showcase. */
  colors?: string[];
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const styleId = useMemo(() => `pcg-bg-css-${++uid}`, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const handle = attachGradient(host, colors);
    if (prefersReducedMotion()) handle.pause();
    return () => handle.destroy();
    // Deliberately empty deps: the palette is fixed per mount, not reactive.
  }, []);

  if (bare) {
    return (
      <div ref={hostRef} className={hostClassName}>
        {children}
      </div>
    );
  }

  return (
    <div className="pcg-bg-showcase">
      <style id={styleId} dangerouslySetInnerHTML={{ __html: CSS }} />
      <div ref={hostRef} className="pcg-bg-hero">
        <div className="pcg-bg-hero-in">
          <span className="pcg-bg-mark" aria-hidden="true" />
          <div>
            <div className="pcg-bg-kicker">{kicker}</div>
            <div className="pcg-bg-title">{title}</div>
            <span className="pcg-bg-live">Live WebGL · running in this report</span>
          </div>
        </div>
      </div>
    </div>
  );
}
